import { useEffect, useState, type ReactNode } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { getApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

interface SearchComboboxProps<T> {
  /** Nome acessível do gatilho (ex.: "Produto IN NATURA"). */
  label: string;
  /** Texto do gatilho quando nada foi escolhido. */
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  errorText: string;
  /** Rótulo da opção escolhida ("" = nenhuma). O pai é a fonte da verdade. */
  value: string;
  disabled?: boolean;
  /**
   * `search`: consulta o backend a cada digitação (com debounce, `q` = termo);
   * `load`: carrega a lista inteira UMA vez ao abrir e filtra localmente pelo rótulo.
   */
  mode: "search" | "load";
  fetchOptions: (q: string) => Promise<T[]>;
  keyOf: (option: T) => string;
  labelOf: (option: T) => string;
  onSelect: (option: T) => void;
  className?: string;
  renderOption?: (option: T) => ReactNode;
}

/**
 * Combobox padrão (Popover + Command) para escolher um registro buscado no backend — mesmo visual dos
 * seletores de produto das demais telas admin. Não filtra localmente (`shouldFilter={false}`): quem decide
 * o que aparece é a consulta ao servidor.
 */
export function SearchCombobox<T>({
  label,
  placeholder,
  searchPlaceholder,
  emptyText,
  errorText,
  value,
  disabled,
  mode,
  fetchOptions,
  keyOf,
  labelOf,
  onSelect,
  className,
  renderOption,
}: SearchComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);

  // `search`: reconsulta a cada termo (debounce). `load`: uma consulta por abertura; digitar só filtra local.
  const term = query.trim();
  const fetchKey = mode === "search" ? term : "";
  useEffect(() => {
    if (!open) return;
    if (mode === "search" && !term) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const list = await fetchOptions(term);
        if (!cancelled) setOptions(list);
      } catch (e) {
        if (!cancelled) toast.error(getApiErrorMessage(e, errorText));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const timer = setTimeout(() => void run(), mode === "search" ? 250 : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchKey, mode]);

  const visible = mode === "load" && term
    ? options.filter((o) => labelOf(o).toLowerCase().includes(term.toLowerCase()))
    : options;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          disabled={disabled}
          className={cn("w-full justify-between gap-2 overflow-hidden font-normal", className)}
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>{value || placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            {loading ? (
              <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Buscando…
              </div>
            ) : visible.length === 0 ? (
              <div className="p-3 text-center text-sm text-muted-foreground">
                {mode === "search" && !term ? "Digite para buscar." : emptyText}
              </div>
            ) : (
              <CommandGroup>
                {visible.map((option) => {
                  const text = labelOf(option);
                  return (
                    <CommandItem
                      key={keyOf(option)}
                      value={keyOf(option)}
                      onSelect={() => {
                        onSelect(option);
                        handleOpenChange(false);
                      }}
                    >
                      <Check className={cn("mr-2 h-4 w-4 shrink-0", text === value ? "opacity-100" : "opacity-0")} />
                      {renderOption ? renderOption(option) : <span className="truncate">{text}</span>}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
